/**
 * Condo/PUD Warranty Form Modal
 *
 * Interactive form for underwriters/admins to certify condo or PUD projects.
 * Integrates signature capture (draw or type) and submits to the
 * conditions-portal edge function which generates a PDF, uploads to
 * Encompass eFolder, and clears condition 0413.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, PenLine, Type, Eraser, AlertCircle, Info } from 'lucide-react';
import { useSignatureCanvas } from '@/hooks/useSignatureCanvas';
import { useTypedSignature, SIGNATURE_FONTS } from '@/hooks/useTypedSignature';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  WARRANTY_TYPE_OPTIONS,
  ENCOMPASS_TO_WARRANTY_MAP,
  type WarrantyType,
  type CondoPudWarrantyModalProps
} from '@/types/condo-pud-warranty';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('CondoPudWarrantyModal', 'conditions-portal');

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const CondoPudWarrantyModal = ({
  open,
  onOpenChange,
  condition,
  loanId,
  loanData,
  encompassData,
  jwt,
  user,
  onSuccess
}: CondoPudWarrantyModalProps) => {
  // Form state
  const [projectName, setProjectName] = useState('');
  const [phaseNumber, setPhaseNumber] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [warrantyType, setWarrantyType] = useState<WarrantyType | ''>('');
  const [cpmCertNumber, setCpmCertNumber] = useState('');
  const [isNonWarrantable, setIsNonWarrantable] = useState(false);
  const [nonWarrantableReason, setNonWarrantableReason] = useState('');
  const [acceptingInvestor, setAcceptingInvestor] = useState('');
  const [signerName, setSignerName] = useState(user.fullName || '');
  const [signerTitle, setSignerTitle] = useState(user.title || '');
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Signature hooks
  const drawSignature = useSignatureCanvas({
    width: 500,
    height: 150,
    validation: { minStrokes: 3 },
    autoInit: false
  });

  const typedSignature = useTypedSignature({
    initialName: user.fullName || '',
    defaultFont: 'Dancing Script',
    canvasWidth: 500,
    canvasHeight: 150
  });

  // Initialize draw canvas when tab is shown
  const drawInitRef = useRef(false);
  useEffect(() => {
    if (open && signatureMode === 'draw' && !drawInitRef.current) {
      // Small delay to let dialog render
      const timer = setTimeout(() => {
        drawSignature.initialize();
        drawInitRef.current = true;
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open, signatureMode, drawSignature]);

  // Reset state when modal opens — pre-fill from Encompass if available
  useEffect(() => {
    if (open) {
      setProjectName('');
      setPhaseNumber('');
      setProjectAddress('');
      setCpmCertNumber('');
      setNonWarrantableReason('');
      setAcceptingInvestor('');
      setSignerName(user.fullName || '');
      setSignerTitle(user.title || '');
      setSignatureMode('draw');
      setError(null);
      setSubmitting(false);
      drawInitRef.current = false;

      // Pre-fill warranty type from Encompass field 1012
      if (encompassData?.projectClassification) {
        const mapped = ENCOMPASS_TO_WARRANTY_MAP[encompassData.projectClassification];
        setWarrantyType(mapped || '');
      } else {
        setWarrantyType('');
      }

      // Pre-fill non-warrantable from Encompass field 3316
      setIsNonWarrantable(encompassData?.isNonWarrantable ?? false);
    }
  }, [open, user.fullName, user.title, encompassData]);

  // Get signature data URL from active mode
  const getSignatureDataUrl = useCallback((): string | null => {
    if (signatureMode === 'draw') {
      return drawSignature.getDataUrl();
    }
    return typedSignature.getDataUrl();
  }, [signatureMode, drawSignature, typedSignature]);

  // Validation
  const isSignatureValid = signatureMode === 'draw' ? drawSignature.hasSignature : typedSignature.isValid();
  const isFormValid =
    projectName.trim().length > 0 &&
    projectAddress.trim().length > 0 &&
    warrantyType !== '' &&
    signerName.trim().length > 0 &&
    isSignatureValid;

  // Submit handler
  const handleSubmit = useCallback(async () => {
    if (!isFormValid || submitting) {
return;
}

    const signatureDataUrl = getSignatureDataUrl();
    if (!signatureDataUrl) {
      setError('Please provide a signature');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      log.info('Submitting warranty form', { loanId, warrantyType, conditionId: condition.id });

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
          apikey: SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          action: 'submit-warranty-form-internal',
          loanId,
          conditionId: condition.id,
          formData: {
            loanNumber: loanData.loanNumber,
            borrowerName: loanData.borrowerName,
            propertyAddress: loanData.propertyAddress,
            projectName: projectName.trim(),
            phaseNumber: phaseNumber.trim(),
            projectAddress: projectAddress.trim(),
            warrantyType,
            cpmCertNumber: warrantyType === 'condo_fannie_type_s' ? cpmCertNumber.trim() : undefined,
            isNonWarrantable,
            nonWarrantableReason: isNonWarrantable ? nonWarrantableReason.trim() : '',
            acceptingInvestor: isNonWarrantable ? acceptingInvestor.trim() : '',
            signerName: signerName.trim(),
            signerTitle: signerTitle.trim(),
            signatureDataUrl,
            signedDate: new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })
          }
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to submit warranty form');
      }

      log.info('Warranty form submitted successfully', {
        loanId,
        attachmentId: result.attachmentId,
        conditionCleared: result.conditionCleared
      });

      onSuccess();
    } catch (err: any) {
      log.error('Warranty form submission failed', { loanId }, err);
      setError(err.message || 'Failed to submit warranty form');
    } finally {
      setSubmitting(false);
    }
  }, [
    isFormValid, submitting, getSignatureDataUrl, jwt, loanId, condition.id,
    loanData, projectName, phaseNumber, projectAddress, warrantyType,
    cpmCertNumber, isNonWarrantable, nonWarrantableReason, acceptingInvestor,
    signerName, signerTitle, onSuccess
  ]);

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-blue-900">
            Conventional Condo/PUD Warranty
          </DialogTitle>
          <DialogDescription>
            Condition 0413 — {condition.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* === LOAN INFO (read-only) === */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Loan Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ReadOnlyField label="Loan Number" value={loanData.loanNumber} />
              <ReadOnlyField label="Borrower Name" value={loanData.borrowerName} />
              <ReadOnlyField
                label="Subject Property Address"
                value={loanData.propertyAddress}
                className="sm:col-span-3"
              />
            </div>
          </div>

          {/* === PROJECT INFO (editable) === */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Project Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="projectName" className="text-xs">Project Name *</Label>
                <Input
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="phaseNumber" className="text-xs">Phase #</Label>
                <Input
                  id="phaseNumber"
                  value={phaseNumber}
                  onChange={(e) => setPhaseNumber(e.target.value)}
                  placeholder="e.g. 1"
                  className="h-8 text-sm"
                />
              </div>
              <div className="sm:col-span-3">
                <Label htmlFor="projectAddress" className="text-xs">Project Address *</Label>
                <Input
                  id="projectAddress"
                  value={projectAddress}
                  onChange={(e) => setProjectAddress(e.target.value)}
                  placeholder="Enter project address"
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>

          {/* === INSTRUCTIONS === */}
          <p className="text-xs text-gray-500 leading-relaxed">
            Condominium and attached PUD projects must be warranted to the applicable RPF
            Condominium/PUD guidelines. This Certification must be fully executed and placed
            in every delivery package secured by a Condominium or attached PUD. Select the
            appropriate warranty type below.
          </p>

          {/* === WARRANTY TYPE SELECTION === */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Warranty Type *</h4>
            <RadioGroup
              value={warrantyType}
              onValueChange={(v) => setWarrantyType(v as WarrantyType)}
              className="space-y-4"
            >
              {Object.entries(WARRANTY_TYPE_OPTIONS).map(([sectionTitle, options]) => (
                <div key={sectionTitle}>
                  <h5 className="text-xs font-semibold text-blue-800 mb-1.5 border-b border-blue-100 pb-1">
                    {sectionTitle}
                  </h5>
                  <div className="space-y-1.5 pl-1">
                    {options.map((opt) => (
                      <label
                        key={opt.value}
                        className={`
                          flex items-start gap-2.5 p-2 rounded-md cursor-pointer transition-colors
                          ${warrantyType === opt.value
                            ? 'bg-emerald-50 border border-emerald-200'
                            : 'hover:bg-gray-50 border border-transparent'
                          }
                        `}
                      >
                        <RadioGroupItem value={opt.value} className="mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-900">{opt.label}</span>
                          {opt.description && (
                            <span className="text-xs text-gray-500 ml-1.5">
                              — {opt.description}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* CMP Cert # field for Type S */}
                  {sectionTitle === 'Condominium - Fannie Mae' && warrantyType === 'condo_fannie_type_s' && (
                    <div className="ml-7 mt-2">
                      <Label htmlFor="cpmCert" className="text-xs">CMP Project Acceptance Cert #</Label>
                      <Input
                        id="cpmCert"
                        value={cpmCertNumber}
                        onChange={(e) => setCpmCertNumber(e.target.value)}
                        placeholder="Enter certificate number"
                        className="h-8 text-sm max-w-xs"
                      />
                    </div>
                  )}
                </div>
              ))}
            </RadioGroup>

            {/* Info note for PUD Freddie types that have no Encompass field 1012 mapping */}
            {(warrantyType === 'pud_freddie_established' || warrantyType === 'pud_freddie_new') && (
              <div className="flex items-start gap-2 mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">
                  This PUD type does not map to an Encompass project classification. The warranty form will be uploaded but field 1012 will not be updated.
                </p>
              </div>
            )}
          </div>

          {/* === NON-WARRANTABLE === */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                id="nonWarrantable"
                checked={isNonWarrantable}
                onCheckedChange={(checked) => setIsNonWarrantable(checked === true)}
              />
              <Label htmlFor="nonWarrantable" className="text-sm font-medium text-gray-900 cursor-pointer">
                Non-Warrantable Project
              </Label>
            </div>
            {isNonWarrantable && (
              <div className="space-y-3 pl-6 pb-2">
                <div>
                  <Label htmlFor="nonWarrantableReason" className="text-xs">Reason for Non-Warrantable Status</Label>
                  <Textarea
                    id="nonWarrantableReason"
                    value={nonWarrantableReason}
                    onChange={(e) => setNonWarrantableReason(e.target.value)}
                    placeholder="Explain why the project is non-warrantable..."
                    className="text-sm min-h-[60px]"
                  />
                </div>
                <div>
                  <Label htmlFor="acceptingInvestor" className="text-xs">Investor That Has Accepted</Label>
                  <Input
                    id="acceptingInvestor"
                    value={acceptingInvestor}
                    onChange={(e) => setAcceptingInvestor(e.target.value)}
                    placeholder="Enter investor name"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* === LENDER CERTIFICATION === */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">Lender Certification</h4>
            <p className="text-xs text-gray-500 mb-4 leading-relaxed">
              The undersigned is certifying that all appropriate documentation has been examined
              and the determination has been made that the project identified is in compliance
              with all required RPF Financial guidelines.
            </p>

            {/* Signer info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <Label htmlFor="signerName" className="text-xs">Name *</Label>
                <Input
                  id="signerName"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="signerTitle" className="text-xs">Title</Label>
                <Input
                  id="signerTitle"
                  value={signerTitle}
                  onChange={(e) => setSignerTitle(e.target.value)}
                  placeholder="e.g. Underwriter"
                  className="h-8 text-sm"
                />
              </div>
            </div>

            {/* Signature capture */}
            <div>
              <Label className="text-xs mb-1 block">Signature *</Label>
              <Tabs
                value={signatureMode}
                onValueChange={(v) => setSignatureMode(v as 'draw' | 'type')}
              >
                <TabsList className="h-8">
                  <TabsTrigger value="draw" className="text-xs gap-1 h-7">
                    <PenLine className="h-3 w-3" /> Draw
                  </TabsTrigger>
                  <TabsTrigger value="type" className="text-xs gap-1 h-7">
                    <Type className="h-3 w-3" /> Type
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="draw" className="mt-2">
                  <div className="border rounded-md bg-white p-1 relative">
                    <canvas
                      ref={drawSignature.canvasRef}
                      width={500}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      style={{ maxHeight: '150px' }}
                      onMouseDown={drawSignature.startDrawing}
                      onMouseMove={drawSignature.draw}
                      onMouseUp={drawSignature.stopDrawing}
                      onMouseLeave={drawSignature.stopDrawing}
                      onTouchStart={drawSignature.startDrawing}
                      onTouchMove={drawSignature.draw}
                      onTouchEnd={drawSignature.stopDrawing}
                    />
                    {drawSignature.hasSignature && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-1 right-1 h-6 text-xs text-gray-400 hover:text-red-500"
                        onClick={drawSignature.clear}
                      >
                        <Eraser className="h-3 w-3 mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                  {!drawSignature.hasSignature && (
                    <p className="text-xs text-gray-400 mt-1">Draw your signature above</p>
                  )}
                </TabsContent>

                <TabsContent value="type" className="mt-2">
                  <div className="space-y-2">
                    <Input
                      value={typedSignature.name}
                      onChange={(e) => typedSignature.setName(e.target.value)}
                      placeholder="Type your name"
                      className="h-8 text-sm"
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {SIGNATURE_FONTS.map((font) => (
                        <button
                          key={font.name}
                          type="button"
                          onClick={() => typedSignature.setSelectedFont(font.name)}
                          className={`
                            px-2 py-1 text-xs rounded border transition-colors
                            ${typedSignature.selectedFont === font.name
                              ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                            }
                          `}
                        >
                          {font.display}
                        </button>
                      ))}
                    </div>
                    <div className="border rounded-md bg-white p-1">
                      <canvas
                        ref={typedSignature.canvasRef}
                        width={500}
                        height={150}
                        className="w-full"
                        style={{ maxHeight: '150px' }}
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Date (auto) */}
            <div className="mt-3">
              <Label className="text-xs">Date</Label>
              <p className="text-sm text-gray-700">
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || submitting}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Sign & Submit'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Small read-only field for loan info display */
const ReadOnlyField = ({
  label,
  value,
  className = ''
}: {
  label: string;
  value: string;
  className?: string;
}) => (
  <div className={className}>
    <Label className="text-xs text-gray-400">{label}</Label>
    <p className="text-sm text-gray-800 bg-gray-50 rounded px-2 py-1 border border-gray-100">
      {value || '—'}
    </p>
  </div>
);

export default CondoPudWarrantyModal;
